import { defineStore } from 'pinia';
import { cloneDeep, get, each, find, map } from 'lodash';
import Vue from 'vue';

import PurchasedItemApi from '@/extensions/shop/store/api/purchasedItem';
import pinia from '@/pinia';
import { useSetupStore } from '@/stores';

// Multistore: the store the admin UI currently has selected. Lists of store-owned
// records default to it, so a satellite stops showing its siblings' rows; the
// store filter widens them again.
const selectedStoreId = () => _.get(useSetupStore(pinia), 'activeCms.id', null);

const defaultForm = {};
const defaultRules = {};
const defaultUi = {
    form: {
        initialized: false,
        validateOnEnter: false
    },
};

export const useShopPurchasedItemStore = defineStore('shopPurchasedItem', {
    state: () => ({
        loading: true,
        purchasedItemsPaginated: {
            variables: {
                paginationSizes: [25, 50, 100, 500]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            link: {},
            items: []
        },
        options: {
            category: {
                loading: false,
                items: []
            },
            status: {
                loading: false,
                items: []
            },
            stock: {
                loading: false,
                items: []
            },
        },
        history: {},
        form: cloneDeep(defaultForm),
        rules: cloneDeep(defaultRules),
        ui: cloneDeep(defaultUi)
    }),

    getters: {
        formInitialized: (state) => get(state, 'ui.form.initialized'),
        formValidateOnEnter: (state) => get(state, 'ui.form.validateOnEnter')
    },

    actions: {
        addPaginatedPurchasedItemsToState(data) {
            this.purchasedItemsPaginated.items = data.items;
            this.purchasedItemsPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.purchasedItemsPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.purchasedItemsPaginated.link = cloneDeep(data.config.link);
            this.purchasedItemsPaginated.showFilters = get(data.config, 'showFilters') || false;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.purchasedItemsPaginated.allColumns, column => {
                let allowed = find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.purchasedItemsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.purchasedItemsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.purchasedItemsPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.purchasedItemsPaginated.filters, key);
            } else {
                Vue.set(this.purchasedItemsPaginated.filters, key, value);
            }
        },

        defineFilterVisibility(value) {
            Vue.set(this.purchasedItemsPaginated, 'showFilters', value);
        },

        defineSorting({ key, value }) {
            Vue.set(this.purchasedItemsPaginated.variables, 'order', key);
            Vue.set(this.purchasedItemsPaginated.variables, 'direction', value);
        },

        definePaginationSizes(value) {
            Vue.set(this.purchasedItemsPaginated.variables, 'paginationSizes', value);
        },

        updateLoading(value) {
            this.loading = value;
        },

        updateRules(rules) {
            Vue.set(this, 'rules', rules);
        },

        resetRules() {
            Vue.set(this, 'rules', cloneDeep(defaultRules));
        },

        resetUI() {
            Vue.set(this, 'ui', cloneDeep(defaultUi));
        },

        insertPurchase(purchase) {
            Vue.set(this, 'form', purchase);
            Vue.set(this, 'history', cloneDeep(purchase));
        },

        insertPurchaseLogs(logs) {
            Vue.set(this.form, 'statusLogs', logs);
        },

        updateStatusOptionsLoading(value) {
            Vue.set(this.options.status, 'loading', value);
        },

        insertStatusOptions(items) {
            Vue.set(this.options.status, 'items', items);
        },

        updateStockOptionsLoading(value) {
            Vue.set(this.options.stock, 'loading', value);
        },

        insertStockOptions(stocks) {
            Vue.set(this.options.stock, 'items', stocks);
        },

        setFormInitialized(flag) {
            Vue.set(this.ui.form, 'initialized', flag);
        },

        setFormValidateOnEnter(flag) {
            Vue.set(this.ui.form, 'validateOnEnter', flag);
        },

        // Async actions
        fetchPurchasedItemsPaginated(payload = {}) {
            return PurchasedItemApi.fetchPurchasedItemsPaginated({ cms: selectedStoreId(), ...payload, ...this.purchasedItemsPaginated }, this.form.id).then(data => {
                this.addPaginatedPurchasedItemsToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        reset() {
            this.insertPurchase(cloneDeep(defaultForm));
        }
    }
});
